import { PoolSearchDto, PoolTrackDataDto } from '@cratehackers/api-client';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonList,
  IonRow,
  IonSpinner,
  IonText,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RecordPool } from '../../types/iPool';

import { addOutline, closeOutline, searchOutline } from 'ionicons/icons';
import ApiService from '../../services/Api.service';
import {
  checkLnrpStatus,
  selectLnrpDownloadPath,
  selectLnrpLoggedIn,
} from '../../store/slices/lnrp.slice'; // adjust path if needed
import { openExternalLink } from '../../utils/openExternalLink';

import { usePlanAware } from '../../hooks/usePlanAware';
import { DmsService } from '../../services/Dms.service';
import { LnrpService } from '../../services/Lnrp.service';
import { useDispatch } from '../../store/hooks';
import {
  checkDmsStatus,
  selectDMSDownloadPath,
  selectDmsLoggedIn,
} from '../../store/slices/dms.slice';
import { addFile } from '../../store/slices/track-matcher.slice';
import isElectron from '../../utils/isElectron';
import { iLocalFile } from '../../types/ILocalFile';
import { DirectDownloadButton } from './DirectDownloadButton';
import { DmsLoginButton } from './DMSLoginButton';
import LNRPLoginModal from './LNRPLoginModal';
import './Pools.scss';
import WaveformInline from '../waveform/WaveformInline';

interface PoolProps {
  artist: string;
  title: string;
  trackId: string;
  poolData: RecordPool;
  searchApi?: boolean;
  tier?: string;
}

const Pool: React.FC<PoolProps> = ({
  artist,
  title,
  poolData,
  trackId,
  searchApi = false,
  tier,
}) => {
  const dispatch = useDispatch();
  const lnrpLoggedIn = useSelector(selectLnrpLoggedIn);
  const dmsLoggedIn = useSelector(selectDmsLoggedIn);

  const [expand, setExpand] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [poolResults, setPoolResults] = useState<PoolTrackDataDto[]>([]);
  const [lnrpLoginOpen, setLnrpLoginOpen] = useState(false);

  const lnrpDownloadFolder = useSelector(selectLnrpDownloadPath);
  const dmsDownloadFolder = useSelector(selectDMSDownloadPath);

  const planAwareGetDMSPreviewURL = usePlanAware(DmsService.getDmsPreviewUrl);

  // whenever we expand LNRP or DMS, re-check session
  useEffect(() => {
    if (expand && poolData.Name === 'Late Night Record Pool') {
      dispatch(checkLnrpStatus());
    }
    if (expand && poolData.Name === 'Direct Music Service') {
      dispatch(checkDmsStatus());
    }
  }, [dispatch, expand, poolData.Name]);

  // search on mount if needed
  useEffect(() => {
    if (searchApi) {
      setLoading(true);
      const chAPI = ApiService.getApiClient();
      const query: PoolSearchDto = {
        artist,
        title,
        poolName: poolData.Name,
      };
      chAPI
        .poolsControllerSearchPools({ poolSearchDto: query })
        .then((returnedData) => {
          setPoolResults(returnedData);
          setError(false);
        })
        .catch((_) => {
          setError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [searchApi, artist, title, poolData.Name]);

  // sanitize once
  const getAndSanitizeLink = (): string => {
    const sanitizedArtist = artist.split(/[\s,&]+|vs|Ft|ft|and/i)[0];
    const titleMatch = title.match(/^[^\[\(\-]+/);
    const simplifiedTitle = titleMatch ? titleMatch[0].trim() : title.trim();
    const sanitizedTitle = simplifiedTitle
      .split(/\s+/)
      .map((w) => w.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ''))
      .filter((w) => w.length > 0)
      .join(' ')
      .trim();
    return poolData.SearchTitleOnly
      ? `${poolData.SearchLink}${sanitizedTitle}`
      : `${poolData.SearchLink}${sanitizedTitle} ${sanitizedArtist}`;
  };
  const link = getAndSanitizeLink();

  const isSearchTier = tier === 'Search';

  return (
    <>
      <IonList lines="none">
        {poolData.SearchLink && (
          <>
            <IonItem
              className="pool-name-text"
              onClick={() => {
                if (
                  poolResults.length > 0 &&
                  poolResults.some(
                    (r) => r.additionalData?.browseUrl !== undefined,
                  )
                ) {
                  setExpand((e) => !e);
                } else {
                  openExternalLink(link);
                }
              }}
            >
              {loading ? (
                <IonSpinner />
              ) : (
                <IonButton
                  fill="clear"
                  style={{
                    visibility: isSearchTier
                      ? 'visible'
                      : poolResults.length > 0
                        ? 'visible'
                        : 'hidden',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSearchTier) {
                      openExternalLink(link);
                    } else {
                      setExpand((e) => !e);
                    }
                  }}
                >
                  <IonIcon
                    icon={
                      isSearchTier
                        ? searchOutline
                        : expand
                          ? closeOutline
                          : addOutline
                    }
                  />
                </IonButton>
              )}
              {poolData.Name}{' '}
              {poolResults.length > 0 && `(${poolResults.length})`}
              {error && (
                <IonText color="warning">&nbsp;(Error Searching)</IonText>
              )}
            </IonItem>

            {expand && poolResults.length > 0 && (
              <IonGrid>
                {poolResults.map((result, i) => {
                  // LNRP / DMS direct-download pools in Electron
                  const isLnrp =
                    poolData.Name === 'Late Night Record Pool' && isElectron();
                  const isDms =
                    poolData.Name === 'Direct Music Service' && isElectron();
                  return (
                    <IonRow key={i} className="pool-result-row">
                      <IonCol size=".5" />
                      <IonCol size="11">
                        <div className="pool-result-row-inner">
                          <div className="pool-result-play">
                            {isLnrp &&
                              lnrpLoggedIn &&
                              result?.additionalData?.preview &&
                              result?.additionalData?.preview !==
                                'REDACTED' && (
                                <WaveformInline
                                  loadUrl={async () =>
                                    result.additionalData?.preview
                                  }
                                  redacted={
                                    result?.additionalData?.preview ===
                                    'REDACTED'
                                  }
                                  height={28}
                                />
                              )}
                            {isDms && dmsLoggedIn && (
                              <WaveformInline
                                loadUrl={() =>
                                  planAwareGetDMSPreviewURL(
                                    result.additionalData?.id,
                                  )
                                }
                                height={28}
                              />
                            )}
                          </div>
                          <div
                            className="pool-result-info"
                            onClick={() =>
                              openExternalLink(
                                result.additionalData?.browseUrl || link,
                              )
                            }
                          >
                            <div className="pool-result-title-artist">
                              <IonText>
                                {result.title} - {result.artist}
                              </IonText>
                            </div>
                            {result.additionalData && (
                              <div className="pool-result-details">
                                <IonText color="medium">
                                  {Object.entries(result.additionalData)
                                    .filter(
                                      ([k]) =>
                                        k !== 'id' &&
                                        k !== 'browseUrl' &&
                                        k !== 'auth' &&
                                        k !== 'date' &&
                                        k !== 'preview',
                                    )
                                    .map(
                                      ([k, v]) => `${k.toUpperCase()}: ${v} `,
                                    )}
                                </IonText>
                              </div>
                            )}
                          </div>
                          <div className="pool-result-action">
                            {isLnrp &&
                              isElectron() &&
                              (lnrpLoggedIn ? (
                                <DirectDownloadButton
                                  mediaId={result?.additionalData?.id}
                                  getDownloadUrl={
                                    LnrpService.lnrpGenerateDownload
                                  }
                                  auth={result?.additionalData?.auth}
                                  downloadFolder={lnrpDownloadFolder}
                                  onDownloaded={(filePath: string) => {
                                    const newTrack: iLocalFile = {
                                      filePath,
                                      id:
                                        new Date().getTime() * filePath.length,
                                    };
                                    dispatch(
                                      addFile({
                                        file: newTrack,
                                        trackId: trackId,
                                      }),
                                    );
                                  }}
                                />
                              ) : (
                                <IonText
                                  color="warning"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLnrpLoginOpen(true);
                                  }}
                                  className="pool-login-btn"
                                >
                                  Sign In To Download
                                </IonText>
                              ))}
                            {isDms &&
                              isElectron() &&
                              (dmsLoggedIn ? (
                                <DirectDownloadButton
                                  mediaId={result?.additionalData?.id}
                                  getDownloadUrl={DmsService.getDmsDownloadUrl}
                                  passedFileName={result?.title}
                                  downloadFolder={dmsDownloadFolder}
                                  onDownloaded={(filePath) => {
                                    const newTrack: iLocalFile = {
                                      filePath,
                                      id:
                                        new Date().getTime() * filePath.length,
                                    };
                                    dispatch(
                                      addFile({
                                        file: newTrack,
                                        trackId: trackId,
                                      }),
                                    );
                                  }}
                                />
                              ) : (
                                <DmsLoginButton />
                              ))}
                          </div>
                        </div>
                      </IonCol>
                      <IonCol size=".5" />
                    </IonRow>
                  );
                })}
              </IonGrid>
            )}
          </>
        )}
      </IonList>
      <LNRPLoginModal
        isOpen={lnrpLoginOpen}
        onDidDismiss={() => setLnrpLoginOpen(false)}
      />
    </>
  );
};

export default Pool;
